import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getMqttInstance, decodeMqttPayload } from '../../../services/mqtt/mqttService';
import { getTenantsAdminList } from '../../../api/backendApi';
import { useAgentAuthorization } from '../../../hooks/useAgentAuthorization';
import { canRequestPlatformTenantList } from '../../../utils/platformTenantAccess';
import { updateAgent } from '../../../services/api/agentService';
import { updateAgentConfig as persistAgentConfig } from '../../../api/agentApi';

// Normalize features to a comma-separated string safely
// Accepts: array ["a","b"], object {a:true,b:false}, or string "a,b"
// Returns: string
const featuresToString = (value, fallback = 'monitoring,alerts') => {
  try {
    if (Array.isArray(value)) return value.join(',');
    if (value && typeof value === 'object') {
      return Object.keys(value)
        .filter(k => value[k])
        .join(',');
    }
    if (typeof value === 'string') return value;
    return fallback;
  } catch (_) {
    return fallback;
  }
};

/**
 * AgentConfigPanel Component
 * Allows admins to send configuration commands to agents
 */
const AgentConfigPanel = ({ agent, onClose, onAgentUpdated, onSave }) => {
  const { userRole } = useAgentAuthorization();
  const platformTenantList = canRequestPlatformTenantList(userRole);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    name: agent?.name || '',
    heartbeat_interval: agent?.heartbeat_interval?.toString() || '60',
    features: featuresToString(agent?.features, 'monitoring,alerts'),
    enable_system_stats: agent?.system ? true : false,
    heartbeat_pattern: agent?.heartbeat_pattern || '',
    use_heartbeat_pattern: agent?.heartbeat_pattern ? true : false,
    // New fields for tenant integration
    tenant_sip_id: agent?.tenant_sip_id || '',
    rest_id: agent?.rest_id || '',
    tenant_id: agent?.tenant_id || '',
    school_building_name: agent?.school_building_name || '',
  });
  const [response, setResponse] = useState(null);
  const [latestAgentData, setLatestAgentData] = useState(agent);
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);

  // Listen for agent status updates to keep our panel in sync
  useEffect(() => {
    let statusListenerCleanup = null;

    const initStatusListener = async () => {
      try {
        const mqtt = await getMqttInstance();
        const statusTopic = `agent/${agent.id}/status`;

        // Subscribe to status messages to get live updates
        console.log(`Subscribing to status topic: ${statusTopic}`);
        mqtt.subscribe(statusTopic, err => {
          if (err) {
            console.error(`Error subscribing to ${statusTopic}:`, err);
          } else {
            console.log(`Successfully subscribed to ${statusTopic}`);
          }
        });

        // Set up message handler
        const messageHandler = (topic, messageBuffer) => {
          try {
            // Parse message
            const messageStr = decodeMqttPayload(messageBuffer);
            let data;

            try {
              data = JSON.parse(messageStr);
            } catch (e) {
              console.warn('Failed to parse MQTT message as JSON:', e);
              return;
            }

            if (data && data.agentId === agent.id) {
              console.log('Agent config panel received status update:', data);
              setLatestAgentData(data);
            }
          } catch (error) {
            console.error('Error processing MQTT message:', error);
          }
        };

        // Register message handler
        mqtt.on('message', messageHandler);

        // Store the cleanup function to be called on component unmount
        statusListenerCleanup = () => {
          try {
            const statusTopic = `agent/${agent.id}/status`;
            mqtt.unsubscribe(statusTopic, err => {
              if (err) {
                console.error(`Error unsubscribing from ${statusTopic}:`, err);
              } else {
                console.log(`Successfully unsubscribed from ${statusTopic}`);
              }
            });

            // Remove message listener
            mqtt.removeListener('message', messageHandler);
          } catch (error) {
            console.error('Error unsubscribing from MQTT topic:', error);
          }
        };

        return statusListenerCleanup;
      } catch (error) {
        console.error('Error setting up status listener:', error);
        return null;
      }
    };

    // Initialize the listener
    initStatusListener();

    // Cleanup function when component unmounts
    return () => {
      if (statusListenerCleanup) {
        statusListenerCleanup();
      }
    };
  }, [agent.id]);

  // Update form when we receive new agent data
  useEffect(() => {
    if (latestAgentData && !loading) {
      setConfig(prev => ({
        ...prev,
        name: latestAgentData.name || prev.name,
        heartbeat_interval:
          latestAgentData.heartbeat_interval?.toString() || prev.heartbeat_interval,
        features:
          typeof latestAgentData.features !== 'undefined'
            ? featuresToString(latestAgentData.features, prev.features)
            : prev.features,
        enable_system_stats: latestAgentData.system ? true : prev.enable_system_stats,
        // Update tenant integration fields if they exist in the latest data
        tenant_sip_id: latestAgentData.tenant_sip_id || prev.tenant_sip_id,
        rest_id: latestAgentData.rest_id || prev.rest_id,
        tenant_id: latestAgentData.tenant_id || prev.tenant_id,
        school_building_name: latestAgentData.school_building_name || prev.school_building_name,
      }));
    }
  }, [latestAgentData, loading]);

  // Load tenants for dropdown (admin list preferred, fallback to user-tenants)
  useEffect(() => {
    let isMounted = true;
    const loadTenants = async () => {
      if (!platformTenantList) {
        if (isMounted) {
          setTenants([]);
          setTenantsLoading(false);
          setTenantsError(null);
        }
        return;
      }
      setTenantsLoading(true);
      setTenantsError(null);
      try {
        let list = [];
        list = await getTenantsAdminList();

        if (!Array.isArray(list)) list = [];
        // Normalize to { id, name }
        const normalized = list
          .filter(t => t && (t.id || t.tenantId))
          .map(t => ({ id: t.id || t.tenantId, name: t.name || t.displayName || (t.id || t.tenantId) }));
        if (isMounted) setTenants(normalized);
      } catch (err) {
        console.error('AgentConfigPanel: Failed to load tenants:', err);
        if (isMounted) setTenantsError('Failed to load tenants');
      } finally {
        if (isMounted) setTenantsLoading(false);
      }
    };

    loadTenants();
    return () => {
      isMounted = false;
    };
  }, [platformTenantList]);

  // Handle form input changes
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Send configuration command to the agent
  const sendConfigCommand = async () => {
    setLoading(true);
    setResponse(null);

    try {
      const mqtt = await getMqttInstance();
      const statusTopic = `agent/${agent.id}/status`;
      const commandTopic = `agent/${agent.id}/command`;
      const resultTopic = `agent/${agent.id}/result`;

      // Format features as an array if it's a string
      let featuresArray = config.features;
      // Extract features as an array
      const featureSource =
        typeof config.features === 'string'
          ? config.features
          : featuresToString(config.features, '');
      const featureArray = featureSource
        .split(',')
        .map(f => f.trim())
        .filter(f => f !== '');

      // Format settings
      const featureToggles = {};
      featureArray.forEach(feature => {
        featureToggles[feature] = true;
      });

      // Format settings
      const settings = {
        persist_config: true, // Explicitly request persistent storage
        enable_system_stats: config.enable_system_stats,
      };

      // Add heartbeat settings - either pattern or interval
      if (config.use_heartbeat_pattern && config.heartbeat_pattern) {
        settings.heartbeat_pattern = config.heartbeat_pattern;
        // If using pattern, still include interval as fallback
        settings.heartbeat_interval = parseInt(config.heartbeat_interval, 10) * 1000;
      } else {
        settings.heartbeat_interval = parseInt(config.heartbeat_interval, 10) * 1000; // Convert to milliseconds
      }

      // Persist to backend (source of truth) before sending MQTT
      try {
        // Minimal agent metadata update
        const backendUpdate = {
          name: config.name,
          ...(config.tenant_sip_id && { tenant_sip_id: config.tenant_sip_id }),
          ...(config.rest_id && { rest_id: config.rest_id }),
          ...(config.tenant_id && { tenant_id: config.tenant_id }),
          ...(config.school_building_name && { school_building_name: config.school_building_name }),
        };

        try {
          await updateAgent(agent.id, backendUpdate);
        } catch (e) {
          console.warn('AgentConfigPanel: updateAgent failed', e);
          toast.warn('Saved to device, but backend agent metadata update failed.');
        }

        try {
          await persistAgentConfig(agent.id, {
            features: featureToggles,
            settings,
            publish_update: false,
          });
        } catch (e) {
          console.warn('AgentConfigPanel: updateAgentConfig failed', e);
          toast.warn('Saved to device, but backend config persistence failed.');
        }
      } catch (persistErr) {
        console.error('AgentConfigPanel: error during backend persistence', persistErr);
      }

      // Create command payload for config messages
      const commandId = `config-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const command = {
        type: 'update',
        commandId,
        agentId: agent.id,
        timestamp: new Date().toISOString(),
        config: {
          name: config.name,
          features: featureToggles,
          // Include either heartbeat_pattern or heartbeat_interval based on selection
          ...(config.use_heartbeat_pattern && config.heartbeat_pattern
            ? { heartbeat_pattern: config.heartbeat_pattern }
            : { heartbeat_interval: parseInt(config.heartbeat_interval, 10) * 1000 }),
          // Always include settings
          persist_env: true, // Tell agent to persist changes to .env file
          settings: settings,
          // Add tenant integration fields when they have values
          ...(config.tenant_sip_id && { tenant_sip_id: config.tenant_sip_id }),
          ...(config.rest_id && { rest_id: config.rest_id }),
          ...(config.tenant_id && { tenant_id: config.tenant_id }),
          ...(config.school_building_name && { school_building_name: config.school_building_name }),
        },
      };

      // Publish config message
      console.log(`Publishing config to ${commandTopic}:`, command);
      mqtt.publish(commandTopic, JSON.stringify(command), { qos: 1 });

      // Set a timeout to handle the case where the agent doesn't respond
      let timeoutId = setTimeout(() => {
        toast.warning(
          `No response from agent ${agent.id} - they may be offline but the config will be applied when they reconnect`
        );
        setLoading(false);
        // Update the UI anyway since the config should be applied when agent reconnects
        if (onAgentUpdated && typeof onAgentUpdated === 'function') {
          const updatedAgentData = {
            ...latestAgentData,
            name: config.name,
            heartbeat_interval: parseInt(config.heartbeat_interval, 10),
            features: Object.keys(featureToggles),
            // Include tenant integration fields in the updated data
            ...(config.tenant_sip_id && { tenant_sip_id: config.tenant_sip_id }),
            ...(config.rest_id && { rest_id: config.rest_id }),
            ...(config.tenant_id && { tenant_id: config.tenant_id }),
            ...(config.school_building_name && {
              school_building_name: config.school_building_name,
            }),
            _configUpdated: Date.now(),
            _persistRequested: true,
          };
          onAgentUpdated(updatedAgentData);
        }
      }, 5000); // 5 second timeout for agent response

      // Subscribe to both result and status topic for responses
      console.log(`Subscribing to result topic: ${resultTopic}`);
      mqtt.subscribe(resultTopic, err => {
        if (err) {
          console.error(`Error subscribing to ${resultTopic}:`, err);
        } else {
          console.log(`Successfully subscribed to ${resultTopic}`);
        }
      });

      // Also watch the status topic for config update confirmations
      console.log(`Subscribing to status topic: ${statusTopic}`);
      mqtt.subscribe(statusTopic, err => {
        if (err) {
          console.error(`Error subscribing to ${statusTopic}:`, err);
        } else {
          console.log(`Successfully subscribed to ${statusTopic}`);
        }
      });

      // Set up message handler for result and status messages
      const resultMessageHandler = (topic, messageBuffer) => {
        console.log(`Received message on topic ${topic}`);

        try {
          // Parse message
          const messageStr = decodeMqttPayload(messageBuffer);
          let data;

          try {
            data = JSON.parse(messageStr);
          } catch (e) {
            console.warn('Failed to parse MQTT message as JSON:', e);
            return;
          }

          console.log('Received response:', data);
          // Helper function for handling successful config updates
          const handleSuccessfulConfigUpdate = (data, message) => {
            clearTimeout(timeoutId);
            setResponse({ ...data, success: true });
            toast.success(
              message || `Configuration updated for ${agent.id}. Changes will persist after reboot.`
            );

            // Force a refresh of agent data after successful config update
            setTimeout(() => {
              // Request a ping to trigger a status update
              const refreshAgentData = async () => {
                try {
                  // Send a ping command to get the latest agent status
                  mqtt.publish(
                    commandTopic,
                    JSON.stringify({
                      type: 'ping',
                      commandId: `ping-${Date.now()}`,
                      agentId: agent.id,
                      timestamp: new Date().toISOString(),
                    }),
                    { qos: 1 }
                  );
                  console.log('Sent ping to refresh agent status after config update');
                } catch (e) {
                  console.error('Failed to ping agent for refresh:', e);
                }
              };
              refreshAgentData();

              // Also update parent component with what we know
              const updatedAgentData = {
                ...latestAgentData,
                name: config.name,
                heartbeat_interval: parseInt(config.heartbeat_interval, 10),
                features: Object.keys(featureToggles),
                // Include tenant integration fields in the updated data
                ...(config.tenant_sip_id && { tenant_sip_id: config.tenant_sip_id }),
                ...(config.rest_id && { rest_id: config.rest_id }),
                ...(config.tenant_id && { tenant_id: config.tenant_id }),
                ...(config.school_building_name && {
                  school_building_name: config.school_building_name,
                }),
                _configUpdated: Date.now(),
                _persistConfirmed: true,
              };

              // Call both callbacks if they exist
              if (onAgentUpdated && typeof onAgentUpdated === 'function') {
                onAgentUpdated(updatedAgentData);
              }

              if (onSave && typeof onSave === 'function') {
                onSave(agent.id, updatedAgentData);
              }
            }, 500); // Small delay to ensure agent has processed the command

            // Clean up listeners
            mqtt.unsubscribe(resultTopic);
            mqtt.unsubscribe(statusTopic);
            mqtt.removeListener('message', resultMessageHandler);

            setLoading(false);
          };

          // Check for status updates with configUpdated flag
          if (topic === statusTopic && data.configUpdated) {
            handleSuccessfulConfigUpdate(data, 'Agent confirmed config update');
            return;
          }

          if (data.commandId === commandId) {
            clearTimeout(timeoutId);
            const isSuccess = Boolean(data.success) || data.status === 'success' || data.configUpdated === true;
            setResponse({ ...data, success: isSuccess });

            if (isSuccess) {
              handleSuccessfulConfigUpdate(data);
            } else {
              toast.error(`Configuration update failed: ${data.output || 'Unknown error'}`);
            }

            // Clean up after receiving a response
            mqtt.unsubscribe(resultTopic, err => {
              if (err) {
                console.error(`Error unsubscribing from ${resultTopic}:`, err);
              } else {
                console.log(`Successfully unsubscribed from ${resultTopic}`);
              }
            });

            // Remove message listener
            mqtt.removeListener('message', resultMessageHandler);

            setLoading(false);
          }
        } catch (error) {
          console.error('Error processing MQTT message:', error);
        }
      };

      // Register message handler
      mqtt.on('message', resultMessageHandler);
    } catch (error) {
      console.error('Error sending configuration command:', error);
      setResponse({
        status: 'error',
        message: `Failed to send command: ${error.message}`,
      });
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Send a ping command to check if agent is responsive
  const sendPingCommand = async () => {
    setLoading(true);
    setResponse(null);

    try {
      const mqtt = await getMqttInstance();
      const pingTopic = `agent/${agent.id}/command`;

      // Create a unique command ID for this ping
      const pingId = `ping-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

      // Create ping command according to API contract
      const pingCommand = {
        type: 'ping',
        commandId: pingId,
        agentId: agent.id,
        timestamp: new Date().toISOString(),
      };

      // Create a timeout to handle no response
      let timeoutId = setTimeout(() => {
        setResponse({
          status: 'timeout',
          message: 'Agent did not respond to ping',
        });
        toast.error(`Ping timed out. Agent ${agent.id} may be offline.`);
        setLoading(false);
      }, 10000);

      // Subscribe to result messages
      const resultTopic = `agent/${agent.id}/result`;
      mqtt.subscribe(resultTopic, err => {
        if (err) {
          console.error(`Error subscribing to ${resultTopic}:`, err);
        } else {
          console.log(`Successfully subscribed to ${resultTopic}`);
        }
      });

      // Set up message handler for result messages
      const resultMessageHandler = (topic, messageBuffer) => {
        console.log(`Received message on topic ${topic}`);

        try {
          // Parse message
          const messageStr = decodeMqttPayload(messageBuffer);
          let data;

          try {
            data = JSON.parse(messageStr);
          } catch (e) {
            console.warn('Failed to parse MQTT message as JSON:', e);
            return;
          }

          console.log('Received ping response:', data);

          // Check if this is the response to our specific ping
          if (data && data.commandId === pingId) {
            // Clear the timeout since we got a response
            clearTimeout(timeoutId);

            // Update state with the response
            setResponse(data);

            if (data.success) {
              toast.success(`Agent ${agent.id} is online and responding!`);
            } else {
              toast.warning(
                `Agent ${agent.id} responded with an error: ${data.output || 'Unknown error'}`
              );
            }

            // Clean up the subscription
            mqtt.unsubscribe(resultTopic, err => {
              if (err) {
                console.error(`Error unsubscribing from ${resultTopic}:`, err);
              } else {
                console.log(`Successfully unsubscribed from ${resultTopic}`);
              }
            });

            // Remove message listener
            mqtt.removeListener('message', resultMessageHandler);

            setLoading(false);
          }
        } catch (error) {
          console.error('Error processing MQTT message:', error);
        }
      };

      // Register message handler
      mqtt.on('message', resultMessageHandler);

      // Send the ping command
      console.log(`Sending ping to agent ${agent.id}:`, pingCommand);
      mqtt.publish(pingTopic, JSON.stringify(pingCommand), { qos: 1 });
    } catch (error) {
      console.error('Error sending ping command:', error);
      setResponse({
        status: 'error',
        message: `Failed to send ping: ${error.message}`,
      });
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle restart command
  const sendRestartCommand = async () => {
    if (!window.confirm(`Are you sure you want to restart agent ${agent.id}?`)) {
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      // Get MQTT instance
      const mqtt = await getMqttInstance();
      const commandTopic = `agent/${agent.id}/command`;
      const resultTopic = `agent/${agent.id}/result`;

      // Create a unique command ID for this restart
      const restartId = `restart-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

      // Create restart command
      const restartCommand = {
        type: 'restart',
        commandId: restartId,
        agentId: agent.id,
        timestamp: new Date().toISOString(),
      };

      console.log(`Restart command to be sent to ${agent.id} with ID ${restartId}`);

      // Subscribe to result topic
      mqtt.subscribe(resultTopic, err => {
        if (err) {
          console.error(`Error subscribing to ${resultTopic}:`, err);
        } else {
          console.log(`Successfully subscribed to ${resultTopic}`);
        }
      });

      let timeoutId;

      // Set up message handler for result messages
      const resultMessageHandler = (topic, messageBuffer) => {
        try {
          // Parse message
          const messageStr = decodeMqttPayload(messageBuffer);
          let data;

          try {
            data = JSON.parse(messageStr);
          } catch (e) {
            console.warn('Failed to parse MQTT message as JSON:', e);
            return;
          }

          console.log('Received restart response:', data);

          if (data && data.commandId === restartId) {
            clearTimeout(timeoutId);
            setResponse(data);
            toast.info(`Agent ${agent.id} is restarting`);

            // Clean up
            mqtt.unsubscribe(resultTopic);
            mqtt.removeListener('message', resultMessageHandler);
          }
        } catch (error) {
          console.error('Error processing MQTT message:', error);
        }
      };

      // Register message handler
      mqtt.on('message', resultMessageHandler);

      // Send the restart command
      console.log(`Publishing restart command to ${commandTopic}:`, restartCommand);
      mqtt.publish(commandTopic, JSON.stringify(restartCommand), { qos: 1 });

      // Set a timeout to unsubscribe if no response
      timeoutId = setTimeout(() => {
        setResponse({
          status: 'timeout',
          message: 'Agent did not respond to restart command',
        });
        toast.error('Command timed out. Agent may be offline.');

        // Clean up
        mqtt.unsubscribe(resultTopic);
        mqtt.removeListener('message', resultMessageHandler);
      }, 15000); // Extended timeout to 15 seconds
    } catch (error) {
      console.error('Error sending restart command:', error);
      setResponse({
        status: 'error',
        message: `Failed to send restart command: ${error.message}`,
      });
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-2 mx-auto max-w-4xl border border-gray-300 ">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Agent Configuration: {agent.id}</h2>
      </div>
{/* 
      {response && (
        <div
          className={`p-4 mb-4 rounded ${response.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
        >
          <h3 className="font-bold">{response.success ? 'Success' : 'Error'}</h3>
          <pre className="mt-2 text-sm whitespace-pre-wrap">
            {JSON.stringify(response, null, 2)}
          </pre>
        </div>
      )} */}

      <form
        onSubmit={e => {
          e.preventDefault();
          sendConfigCommand();
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="name">
              Agent Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={config.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          <div>
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="use_heartbeat_pattern"
                name="use_heartbeat_pattern"
                checked={config.use_heartbeat_pattern}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={loading}
              />
              <label className="ml-2 block text-gray-700" htmlFor="use_heartbeat_pattern">
                Use Advanced Heartbeat Schedule
              </label>
            </div>

            {config.use_heartbeat_pattern ? (
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="heartbeat_pattern">
                  Heartbeat Pattern
                </label>
                <textarea
                  id="heartbeat_pattern"
                  name="heartbeat_pattern"
                  value={config.heartbeat_pattern}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Mon-Fri,06:00-18:00=20000;Mon-Fri,18:00-06:00=5000;Sat-Sun,00:00-23:59=8000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Format: Day-Range,Time-Range=Interval(ms);... (e.g. Mon-Fri,09:00-17:00=30000)
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="heartbeat_interval">
                  Heartbeat Interval (seconds)
                </label>
                <input
                  type="number"
                  id="heartbeat_interval"
                  name="heartbeat_interval"
                  value={config.heartbeat_interval}
                  onChange={handleChange}
                  min="1"
                  max="60"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-gray-700 mb-2" htmlFor="features">
              Features (comma separated)
            </label>
            <input
              type="text"
              id="features"
              name="features"
              value={config.features}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <p className="text-sm text-gray-500 mt-1">e.g. monitoring,alerts,metrics</p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enable_system_stats"
              name="enable_system_stats"
              checked={config.enable_system_stats}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={loading}
            />
            <label className="ml-2 block text-gray-700" htmlFor="enable_system_stats">
              Enable System Stats Collection
            </label>
          </div>

          {/* Tenant Integration Section */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 mt-2">Tenant Integration</h3>
          </div>

          <div>
            <label className="block text-gray-700 mb-2" htmlFor="tenant_sip_id">
              Tenant SIP ID
            </label>
            <input
              type="text"
              id="tenant_sip_id"
              name="tenant_sip_id"
              value={config.tenant_sip_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              placeholder="Enter tenant SIP ID"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2" htmlFor="rest_id">
              REST ID
            </label>
            <input
              type="text"
              id="rest_id"
              name="rest_id"
              value={config.rest_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              placeholder="Enter REST ID"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2" htmlFor="tenant_id">
              Tenant ID
            </label>
            {tenantsLoading ? (
              <input
                type="text"
                id="tenant_id"
                name="tenant_id"
                value={config.tenant_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading || !platformTenantList}
                placeholder="Loading tenants..."
              />
            ) : tenantsError || tenants.length === 0 ? (
              <input
                type="text"
                id="tenant_id"
                name="tenant_id"
                value={config.tenant_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading || !platformTenantList}
                placeholder={tenantsError ? 'Enter tenant ID' : 'Enter tenant ID'}
              />
            ) : (
              <select
                id="tenant_id"
                name="tenant_id"
                value={config.tenant_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading || !platformTenantList}
              >
                <option value="">— Select a Tenant —</option>
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name || t.id}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-gray-700 mb-2" htmlFor="school_building_name">
              School/Building Name
            </label>
            <input
              type="text"
              id="school_building_name"
              name="school_building_name"
              value={config.school_building_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              placeholder="Enter school or building name"
            />
          </div>
        </div>

        <div className="flex justify-between mt-8">
          <div className="space-x-3">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Update Configuration'}
            </button>

            <button
              type="button"
              onClick={sendPingCommand}
              disabled={loading}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Ping Agent
            </button>
          </div>

          <div className="space-x-3">
            <button
              type="button"
              onClick={sendRestartCommand}
              disabled={loading}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
            >
              Restart Agent
            </button>

            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AgentConfigPanel;
